// Collision groups as named layers. The game passes one table: each layer and the layers it collides with. Layers
// become Rapier's 16-bit interaction groups (membership in the high half, filter in the low half), in the table's key
// order. Collision is symmetric: two layers collide if either lists the other, so the table can't disagree with itself.
//
// The top bit is kept for queries: every collider's filter accepts it and no collider is a member of it, so a ray or a
// shape cast sees colliders on any layer it asks for, even a layer that collides with nothing, and no two colliders
// ever collide through it. That leaves 15 layers for the game.

/** Layer name → the layers it collides with. At most 15 layers. */
export type LayerTable = Readonly<Record<string, readonly string[]>>;

/** Rapier interaction groups (a 32-bit mask: membership << 16 | filter). */
export type InteractionGroups = number;

/** The most layers a table may name. */
export const MAX_LAYERS = 15;

const QUERY_BIT = 1 << 15;

/** The resolved table: a bit per layer and the groups each layer's colliders carry. */
export interface Layers {
  readonly names: readonly string[];
  /** The bit of one layer. */
  bit(layer: string): number;
  /** Whether colliders on these two layers collide. */
  collides(a: string, b: string): boolean;
  /** The interaction groups for a collider on `layer`. */
  groups(layer: string): InteractionGroups;
  /** The groups for a query that sees colliders on any of `layers` (every layer when omitted). */
  query(layers?: readonly string[]): InteractionGroups;
}

/** Resolve a layer table. Throws on an empty table, more than 15 layers or a name the table doesn't define. */
export function createLayers(table: LayerTable): Layers {
  const names = Object.keys(table);
  if (names.length === 0) throw new Error("collision layers: the table is empty");
  if (names.length > MAX_LAYERS) throw new Error(`collision layers: ${names.length} layers, at most ${MAX_LAYERS}`);
  const bits = new Map(names.map((n, i) => [n, 1 << i]));
  const bit = (layer: string): number => {
    const b = bits.get(layer);
    if (b === undefined) throw new Error(`collision layers: unknown layer "${layer}" (have ${names.join(", ")})`);
    return b;
  };
  const filter = new Map<string, number>(names.map((n) => [n, QUERY_BIT]));
  for (const [layer, hits] of Object.entries(table)) {
    for (const other of hits) {
      filter.set(layer, filter.get(layer)! | bit(other));
      filter.set(other, filter.get(other)! | bit(layer));
    }
  }
  const all = (1 << names.length) - 1;
  return {
    names,
    bit,
    collides: (a, b) => (filter.get(a)! & bit(b)) !== 0,
    groups: (layer) => ((bit(layer) << 16) | filter.get(layer)!) >>> 0,
    query: (layers) => ((QUERY_BIT << 16) | (layers ? layers.reduce((m, l) => m | bit(l), 0) : all)) >>> 0,
  };
}
